import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { DateRange } from 'react-day-picker';
import { format, parseISO } from 'date-fns';
import moment, { Moment } from 'moment';
import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  AVAILABLE_BRAND_FEATURES,
  ProductLine,
} from '@kouto/types';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { analyticsManager } from 'features/analytics';
import { useBrandId } from 'AppProvider';
import { useIsMobile } from 'WindowDimensionProvider';
import useBrandCategories from 'api/categories/useBrandCategories';
import { SecondaryButton, SecondaryButtonLink } from 'components/theme/Button';
import CalendarIcon from 'assets/calendar';
import useCustomHistory from 'hooks/use-custom-history';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSearchQueryParams from 'hooks/use-search-params';
import { toSearchPathname } from 'utils/listings';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import { useLandingPageRouteParams } from 'hooks/useLandingPageRouteParams';
import useScheduleViewConfig from 'hooks/useScheduleViewConfig';
import { DayPickerPopover } from 'components/molecules/DayPickerPopover';
import { Popover } from 'components/atoms/Popover';
import { DropdownCaret } from 'components/atoms/DropdownCaret';
import {
  FilterListbox,
  FilterListboxOption,
} from 'components/molecules/FilterListbox';
import { SrOnly } from 'components/common/styled/common-styled';
import useBrandVibes from 'api/vibes/useBrandVibes';
import BottomSheetCategorySelector from './BottomSheetCategorySelector';
import resolveFilterItemCount from './resolveFilterItemCount';

const toClassNamePrefix = (showScheduleView: boolean): string =>
  showScheduleView ? 'schedule-view__' : '';

/**
 * Row rendered inside the /search "More" dropdown — toggles the item in the categoryIds /
 * vibeIds URL param. Doesn't navigate on click (we're already on /search). Presentation is
 * delegated to the shared <FilterListboxOption> molecule; this wrapper owns the URL toggle
 * logic that's specific to the landing filters.
 */
const MoreFilterOption = ({
  item,
  searchParamKey,
  showScheduleView,
}: {
  item:
    | ReturnType<typeof useBrandCategories>['categories'][number]
    | ReturnType<typeof useBrandVibes>['vibes'][number];
  searchParamKey: string;
  showScheduleView: boolean;
}) => {
  const { searchParams, setParam } = useSearchQueryParams();
  const classNamePrefix = toClassNamePrefix(showScheduleView);
  const isActive = (searchParams[searchParamKey] ?? []).includes(item.id);

  const onSelect = () => {
    const current = Array.isArray(searchParams[searchParamKey])
      ? searchParams[searchParamKey]
      : [searchParams[searchParamKey]];
    const next = isActive
      ? current.filter((id: string) => id !== item.id)
      : [...current, item.id];

    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_FILTER, {
      [ANALYTICS_PROPERTY.FilterOptions]: item.name,
    });

    if (next.length >= 1) {
      setParam(searchParamKey, next.filter(Boolean));
    } else {
      setParam(searchParamKey, null);
    }
  };

  return (
    <FilterListboxOption
      ariaLabel={item.name}
      className={`${classNamePrefix}listing-filter-more-option ${classNamePrefix}listing-filter-more-option--${
        searchParamKey === 'categoryIds' ? 'category' : 'vibe'
      }`}
      onSelect={onSelect}
      selected={isActive}
    >
      <span className={`${classNamePrefix}listing-filter-more-option-label`}>
        {item.name}
      </span>
    </FilterListboxOption>
  );
};

const ControlledFilterButton = ({
  asTab = false,
  item,
  searchParamKey,
  showScheduleView,
}: {
  item:
    | ReturnType<typeof useBrandCategories>['categories'][number]
    | ReturnType<typeof useBrandVibes>['vibes'][number];
  searchParamKey: string;
  showScheduleView: boolean;
  /** Render as an inline filter-row tab; otherwise a pill (e.g. inside the mobile bottom sheet). */
  asTab?: boolean;
}) => {
  const history = useCustomHistory();
  const location = useLocation();
  const { searchParams, setParam } = useSearchQueryParams();
  const classNamePrefix = toClassNamePrefix(showScheduleView);

  const onClick = () => {
    const currentSearchParamValues = Array.isArray(searchParams[searchParamKey])
      ? searchParams[searchParamKey]
      : [searchParams[searchParamKey]];

    const searchParamValues = isActive
      ? currentSearchParamValues.filter((id: string) => id !== item.id)
      : [...currentSearchParamValues, item.id];

    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_FILTER, {
      [ANALYTICS_PROPERTY.FilterOptions]: item.name,
    });

    const onResultsPage =
      location.pathname.includes('search') || location.pathname.includes('/e');

    if (onResultsPage) {
      // Already on the results page: update the filter param in place.
      if (searchParamValues.length >= 1) {
        setParam(searchParamKey, searchParamValues.filter(Boolean));
      } else {
        setParam(searchParamKey, null);
      }
      return;
    }

    // From home: push the results route carrying the selected filter directly, instead
    // of writing the filter onto the current (home) URL first via setParam. Rewriting the
    // home entry made the browser Back land on `?<filter>=…` instead of the bare home URL.
    const nextSearch = { ...searchParams };
    if (searchParamValues.length >= 1) {
      nextSearch[searchParamKey] = searchParamValues.filter(Boolean);
    } else {
      delete nextSearch[searchParamKey];
    }

    history.push({
      pathname: toSearchPathname(location.pathname),
      search: nextSearch,
    });
  };

  const isActive = (searchParams[searchParamKey] ?? []).includes(item.id);
  const commonProps = {
    'aria-pressed': isActive,
    className: `${classNamePrefix}listing-filter-${
      searchParamKey === 'categoryIds' ? 'category' : 'vibe'
    }-button`,
    onClick,
    selected: isActive,
    tabIndex: 0,
  };

  if (asTab) {
    return (
      <TabFilterButton {...commonProps} $asTab={showScheduleView}>
        {showScheduleView ? (
          <TabFilterLabel selected={isActive}>{item.name}</TabFilterLabel>
        ) : (
          item.name
        )}
      </TabFilterButton>
    );
  }

  return <SecondaryButton {...commonProps}>{item.name}</SecondaryButton>;
};

const DatePickerButton = ({
  align,
  showClearButton,
  showScheduleView,
}: {
  align: 'start' | 'end';
  showClearButton: boolean;
  showScheduleView: boolean;
}) => {
  const isMobile = useIsMobile();
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const classNamePrefix = toClassNamePrefix(showScheduleView);
  const [hovered, setHovered] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDateCalendar, setStartDateCalendar] = useState<Moment | null>(
    null,
  );
  const [endDateCalendar, setEndDateCalendar] = useState<Moment | null>(null);
  const { searchParams, setParam } = useSearchQueryParams();
  const location = useLocation();
  const history = useCustomHistory();

  const datePickerRef = useRef<HTMLDivElement>(null);
  const [lastFocusedElement, setLastFocusedElement] =
    useState<HTMLElement | null>(null);

  const cancelPicker = () => {
    setStartDateCalendar(null);
    setEndDateCalendar(null);
    setShowDatePicker(false);
    lastFocusedElement?.focus();
  };

  useEffect(() => {
    const datePickerRefCurrent = datePickerRef.current;
    if (!showDatePicker) return () => {};

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelPicker();
        lastFocusedElement?.focus();
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(e.relatedTarget as Node)
      ) {
        cancelPicker();
        if (!e.relatedTarget) {
          lastFocusedElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    datePickerRefCurrent?.addEventListener('blur', handleFocusOut);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      datePickerRefCurrent?.removeEventListener('blur', handleFocusOut);
    };
  }, [showDatePicker, cancelPicker, lastFocusedElement]);

  const handleOnDateChange = ({
    startDate,
    endDate,
  }: {
    startDate: Moment | null;
    endDate: Moment | null;
  }) => {
    if (startDate) {
      setStartDateCalendar(startDate);
    } else {
      setStartDateCalendar(null);
    }
    if (endDate) {
      setEndDateCalendar(endDate);
    } else {
      setEndDateCalendar(null);
    }
  };

  const applyDateFilter = () => {
    const updatedParams = { ...searchParams };

    if (startDateCalendar) {
      updatedParams.from = startDateCalendar.format('YYYY-MM-DD');
    }
    if (endDateCalendar) {
      updatedParams.to = endDateCalendar.format('YYYY-MM-DD');
    }

    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_FILTER, {
      [ANALYTICS_PROPERTY.FilterOptions]: 'date',
    });

    // Close the picker regardless of path
    setShowDatePicker(false);
    lastFocusedElement?.focus();

    // Only do navigation if needed
    if (
      !location.pathname.includes('search') &&
      !location.pathname.includes('/e')
    ) {
      history.push({
        pathname: toSearchPathname(location.pathname),
        search: updatedParams,
      });
    } else {
      // Already on search page, update params
      if (startDateCalendar) {
        setParam('from', startDateCalendar.format('YYYY-MM-DD'));
      }
      if (endDateCalendar) {
        setParam('to', endDateCalendar.format('YYYY-MM-DD'));
      }
    }
  };

  const openPickerIfNeeded = () => {
    if (!showDatePicker) {
      setLastFocusedElement(document.activeElement as HTMLElement);

      if (searchParams.from) {
        setStartDateCalendar(moment(searchParams.from));
      }
      if (searchParams.to) {
        setEndDateCalendar(moment(searchParams.to));
      }
      setShowDatePicker(true);
    }
  };

  const clearPicker = () => {
    setStartDateCalendar(null);
    setEndDateCalendar(null);
    setParam('from', undefined);
    setParam('to', undefined);
    setShowDatePicker(false);
    lastFocusedElement?.focus();
  };

  const onMouseEnter = () => {
    setHovered(true);
  };

  const onMouseLeave = () => {
    setHovered(false);
  };

  const onSelect = (range: DateRange | undefined) => {
    handleOnDateChange({
      startDate: range?.from ? moment(range?.from) : null,
      endDate: range?.to ? moment(range?.to) : null,
    });
  };

  // Block past dates from the picker (they get the line-through styling on `.disabled--date`
  // and react-day-picker emits `disabled` on the day button, which screen readers announce).
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const labelDayButton = (
    date: Date,
    modifiers: { disabled?: boolean } | undefined,
  ) => {
    const formatted = format(date, 'PPPP', { locale });
    if (modifiers?.disabled) {
      return translate('pastDateAriaLabel', {
        defaultValue: '{{date}}, past date, not available',
        date: formatted,
      });
    }
    return translate('chooseDateAriaLabel', {
      defaultValue: 'Choose {{date}}',
      date: formatted,
    });
  };

  const isActive = searchParams.from && searchParams.to;
  const iconColor = isActive
    ? `var(--way-colors-primaryColorContrastShades-${hovered ? 100 : 80})`
    : `var(--way-colors-contrastColorShades-${hovered ? 100 : 80})`;

  const formatFilterDate = (value: string) =>
    format(parseISO(value), 'MMM dd', { locale });

  const ariaLabel = isActive
    ? `${formatFilterDate(searchParams.from)} - ${formatFilterDate(
        searchParams.to,
      )}, date filter`
    : undefined;

  const datePickerTriggerButton = (
    <DateButton
      $asTab={showScheduleView}
      className={`${classNamePrefix}listing-filter-date-button`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      selected={isActive}
      onClick={openPickerIfNeeded}
      aria-label={ariaLabel}
      aria-haspopup="dialog"
      aria-expanded={showDatePicker}
      role="button"
    >
      <CalendarIcon
        color={iconColor}
        className={
          isActive ? 'calendar-icon--active' : 'calendar-icon--inactive'
        }
      />
      {(() => {
        const label = isActive
          ? ` ${formatFilterDate(searchParams.from)} - ${formatFilterDate(
              searchParams.to,
            )}`
          : translate('selectDate', { defaultValue: 'Select Date' });
        return showScheduleView ? (
          <TabFilterLabel selected={isActive}>{label}</TabFilterLabel>
        ) : (
          <span>{label}</span>
        );
      })()}
    </DateButton>
  );

  return (
    <DayPickerPopover
      mode="range"
      numberOfMonths={isMobile ? 1 : 2}
      open={showDatePicker}
      onOpenChange={setShowDatePicker}
      onClearButtonClick={showClearButton ? clearPicker : undefined}
      onCancelButtonClick={cancelPicker}
      onApplyButtonClick={applyDateFilter}
      isClearButtonDisabled={!startDateCalendar && !endDateCalendar}
      isApplyButtonDisabled={!startDateCalendar || !endDateCalendar}
      className={`${classNamePrefix}landing-page-filter-date-picker`}
      asChild
      sideOffset={10}
      side="bottom"
      align={align}
      trigger={datePickerTriggerButton}
      onSelect={onSelect}
      selected={{
        from: startDateCalendar?.toDate?.(),
        to: endDateCalendar?.toDate?.(),
      }}
      disabled={{ before: startOfToday }}
      labels={{ labelDayButton }}
    />
  );
};

const CalendarButton = ({
  showScheduleView,
}: {
  showScheduleView: boolean;
}) => {
  const location = useLocation();
  const history = useCustomHistory();
  const { t: translate } = useTranslation();
  const classNamePrefix = toClassNamePrefix(showScheduleView);

  const goToResultsPage = () => {
    analyticsManager.sendEvent(ANALYTICS_EVENT.VIEW_CALENDAR, {
      [ANALYTICS_PROPERTY.PageType]: 'Calendar Page',
    });
    history.push({
      pathname: toSearchPathname(location.pathname),
    });
  };

  const [hovered, setHovered] = useState(false);

  const onMouseEnter = () => {
    setHovered(true);
  };

  const onMouseLeave = () => {
    setHovered(false);
  };

  const { searchParams } = useSearchQueryParams();

  const isActive = searchParams.from && searchParams.to;
  const iconColor = isActive
    ? `var(--way-colors-primaryColorContrastShades-${hovered ? 100 : 80})`
    : `var(--way-colors-contrastColorShades-${hovered ? 100 : 80})`;

  return (
    <DateButton
      $asTab={showScheduleView}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`${classNamePrefix}listing-filter-calendar-button`}
      onClick={goToResultsPage}
      selected={false}
    >
      <CalendarIcon color={iconColor} />
      {showScheduleView ? (
        <TabFilterLabel selected={false}>
          {translate('viewCalendar')}
        </TabFilterLabel>
      ) : (
        translate('viewCalendar')
      )}
    </DateButton>
  );
};

const DateOrCalendarButton = ({
  align,
  product,
  showClearButton,
  showScheduleView,
}: {
  align: 'start' | 'end';
  product?: ProductLine;
  showClearButton: boolean;
  showScheduleView: boolean;
}) => {
  const params = useLandingPageRouteParams();

  if (product === ProductLine.ACTIVATE && !params.isResults) {
    return <CalendarButton showScheduleView={showScheduleView} />;
  }
  return (
    <DatePickerButton
      align={align}
      showClearButton={showClearButton}
      showScheduleView={showScheduleView}
    />
  );
};

/** Leading "All" tab — active when no category/vibe filter is selected; clears them on click. */
const AllFilterButton = ({
  showScheduleView,
}: {
  showScheduleView: boolean;
}) => {
  const { t: translate } = useTranslation();
  const { searchParams, setParam } = useSearchQueryParams();
  const classNamePrefix = toClassNamePrefix(showScheduleView);

  const toIds = (value: unknown): string[] =>
    (Array.isArray(value) ? value : [value]).filter(Boolean) as string[];
  const isActive =
    toIds(searchParams.categoryIds).length === 0 &&
    toIds(searchParams.vibeIds).length === 0;

  const onClick = () => {
    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_FILTER, {
      [ANALYTICS_PROPERTY.FilterOptions]: 'all',
    });
    setParam('categoryIds', null);
    setParam('vibeIds', null);
  };

  return (
    <TabFilterButton
      $asTab={showScheduleView}
      className={`${classNamePrefix}listing-filter-all-button`}
      onClick={onClick}
      selected={isActive}
      tabIndex={0}
      aria-pressed={isActive}
    >
      {showScheduleView ? (
        <TabFilterLabel selected={isActive}>
          {translate('all', { defaultValue: 'All' })}
        </TabFilterLabel>
      ) : (
        translate('all', { defaultValue: 'All' })
      )}
    </TabFilterButton>
  );
};

const LandingPageFilters = () => {
  const brandId = useBrandId();
  const isMobile = useIsMobile();
  const history = useCustomHistory();
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [categoriesBottomDrawerOpen, setCategoriesBottomDrawerOpen] =
    useState(false);
  const { t: translate } = useTranslation();
  const location = useLocation();
  const params = useLandingPageRouteParams();
  const { showScheduleView } = useScheduleViewConfig();
  const classNamePrefix = toClassNamePrefix(showScheduleView);
  const isScheduleResultsView = Boolean(params.isResults) && showScheduleView;
  const { searchParams } = useSearchQueryParams();
  const { categories } = useBrandCategories(brandId, params.product);
  const { vibes } = useBrandVibes();

  const vibesEnabled = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.ENABLE_VIBES_V2,
  );

  const filterItems = [
    ...categories.map((category) => ({
      ...category,
      searchParamKey: 'categoryIds',
    })),
    ...(vibesEnabled
      ? vibes.map((vibe) => ({
          ...vibe,
          searchParamKey: 'vibeIds',
        }))
      : []),
  ];

  const filterItemCount = resolveFilterItemCount(
    filterItems,
    isMobile,
    params.isResults || false,
    showScheduleView,
  );

  const displayedFilters = filterItems.slice(0, filterItemCount);
  const overflowFilters = filterItems.slice(filterItemCount);

  const selectedCategoryIds = Array.isArray(searchParams.categoryIds)
    ? searchParams.categoryIds
    : [searchParams.categoryIds];

  const selectedVibeIds = Array.isArray(searchParams.vibeIds)
    ? searchParams.vibeIds
    : [searchParams.vibeIds];

  const moreButtonIsActive = filterItems.some(
    (item) =>
      (selectedCategoryIds.includes(item.id) ||
        selectedVibeIds.includes(item.id)) &&
      filterItems.indexOf(item) > filterItemCount - 1,
  );

  const goToResultsPage = () => {
    history.push({
      pathname: toSearchPathname(location.pathname),
      search: searchParams,
    });
  };

  // Three "More" behaviours, mutually exclusive:
  //  - Schedule /search (any viewport): opens the anchored <FilterListbox> popover with the
  //    multi-select rows shared with the schedule view's own filter dropdown.
  //  - Mobile /search on a non-schedule brand: opens the bottom-sheet category drawer — the
  //    original UX for narrow viewports where an anchored dropdown crowds the row.
  //  - Landing pages: navigates to /search (there's more room + filters cascade naturally there).
  const moreOpensDropdown = isScheduleResultsView;
  const moreOpensDrawer =
    isMobile && Boolean(params.isResults) && !isScheduleResultsView;
  const showMoreButton =
    filterItems.length > filterItemCount &&
    (isScheduleResultsView || !params.isResults || isMobile);

  return (
    <>
      <SrOnly>{translate('filterExperienceTypeScreenReader')}</SrOnly>

      {/* Schedule view keeps the date control in its own container so it can anchor the left
          rail (col 1) of LandingPageHeader's 1fr auto 1fr grid. In the standard non-schedule
          layout the date button is folded into the filter cluster so it reads as the first
          item in a single row alongside All / categories / More. */}
      {showScheduleView && (
        <DateContainer
          aria-label={translate('filterByDateRange')}
          className={`${classNamePrefix}listing-filters-container`}
          role="region"
        >
          <DateOrCalendarButton
            align="start"
            product={params.product}
            showClearButton
            showScheduleView={showScheduleView}
          />
        </DateContainer>
      )}

      <ButtonsContainer
        aria-label={translate('filterExperiences')}
        className={`${classNamePrefix}listing-filter-buttons-container`}
        role="region"
      >
        {!showScheduleView && (
          <DateOrCalendarButton
            align="start"
            product={params.product}
            showClearButton
            showScheduleView={showScheduleView}
          />
        )}
        <AllFilterButton showScheduleView={showScheduleView} />

        {displayedFilters.map((item) => (
          <ControlledFilterButton
            asTab
            item={item}
            key={item.id}
            searchParamKey={item.searchParamKey}
            showScheduleView={showScheduleView}
          />
        ))}

        {showMoreButton && moreOpensDropdown && (
          <Popover
            asChild
            portalled
            open={moreDropdownOpen}
            onOpenChange={setMoreDropdownOpen}
            align="end"
            side="bottom"
            sideOffset={6}
            aria-label={translate('moreFiltersAndExperiences')}
            trigger={
              <TabFilterButton
                $asTab={showScheduleView}
                aria-expanded={moreDropdownOpen}
                aria-haspopup="listbox"
                className={`${classNamePrefix}listing-filter-more-button`}
                data-testid="listing-filter-more-button"
                selected={moreButtonIsActive}
              >
                {showScheduleView ? (
                  <TabFilterLabel selected={moreButtonIsActive}>
                    {translate('more')}
                  </TabFilterLabel>
                ) : (
                  translate('more')
                )}
                <DropdownCaret
                  className={`${classNamePrefix}listing-filter-more-caret`}
                  open={moreDropdownOpen}
                />
              </TabFilterButton>
            }
          >
            <FilterListbox
              ariaLabel={translate('moreFiltersAndExperiences')}
              multiSelectable
            >
              {overflowFilters.map((item) => (
                <MoreFilterOption
                  item={item}
                  key={item.id}
                  searchParamKey={item.searchParamKey}
                  showScheduleView={showScheduleView}
                />
              ))}
            </FilterListbox>
          </Popover>
        )}

        {showMoreButton && moreOpensDrawer && (
          <TabFilterButton
            $asTab={showScheduleView}
            aria-expanded={categoriesBottomDrawerOpen}
            aria-haspopup="dialog"
            className={`${classNamePrefix}listing-filter-more-button`}
            data-testid="listing-filter-more-button"
            onClick={() => setCategoriesBottomDrawerOpen(true)}
            selected={moreButtonIsActive}
          >
            {translate('more')}
          </TabFilterButton>
        )}

        {showMoreButton && !moreOpensDropdown && !moreOpensDrawer && (
          <TabFilterLink
            $asTab={showScheduleView}
            aria-label={translate('moreFiltersAndExperiences')}
            className={`${classNamePrefix}listing-filter-more-button`}
            data-testid="listing-filter-more-button"
            onClick={(e) => {
              e.preventDefault();
              goToResultsPage();
            }}
            selected={moreButtonIsActive}
          >
            {showScheduleView ? (
              <TabFilterLabel selected={moreButtonIsActive}>
                {translate('more')}
              </TabFilterLabel>
            ) : (
              translate('more')
            )}
          </TabFilterLink>
        )}
      </ButtonsContainer>

      {moreOpensDrawer && categoriesBottomDrawerOpen && (
        <BottomSheetCategorySelector
          onClose={() => setCategoriesBottomDrawerOpen(false)}
        >
          {filterItems.map((item) => (
            <ControlledFilterButton
              item={item}
              key={item.id}
              searchParamKey={item.searchParamKey}
              showScheduleView={showScheduleView}
            />
          ))}
        </BottomSheetCategorySelector>
      )}
    </>
  );
};

export default LandingPageFilters;

/* Left-anchored container that holds only the date picker / calendar trigger. Natural width
   so the date button hugs the start of LandingPageHeader's flex row. */
const DateContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
`;

/* Filter buttons container (All, category/vibe chips, More). Sits as a plain flex/grid child
   of LandingPageHeader — horizontal placement is decided by the parent: flex + title's
   margin-right:auto pushes this group right; without a title it stays adjacent to the date
   container; in the schedule grid (1fr auto 1fr) it lands page-centered in col 2. */
const ButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px;
`;

/* Filter-row tab visual — categories/vibes/All/More/DatePicker in schedule view. Selection is
   conveyed purely by text color + the underline on the inner <TabFilterLabel>; no background
   in any interaction state so clicks don't flash a pill under the label. */
const tabStyle = css<{ selected?: boolean }>`
  display: inline-flex;
  background: none;
  border: none;
  border-radius: 0;
  height: 32px;
  min-width: 32px;
  padding: 0 12px;
  white-space: nowrap;
  text-decoration: none;
  font-family: inherit;
  transition: color 120ms ease;
  color: ${({ selected }) =>
    selected
      ? 'var(--way-colors-contrastColorShades-100)'
      : 'var(--way-colors-contrastColorShades-50)'};

  &:hover,
  &:active,
  &:focus-visible {
    background: none;
    border: none;
    text-decoration: none;
    color: var(--way-colors-contrastColorShades-100);
  }
  &:focus-visible {
    outline: 2px solid var(--way-colors-focusBorderColor);
    outline-offset: 2px;
  }
`;

/* Inline-block wrapper around the tab label text. The transparent border-bottom on the
   unselected state acts as a spacer so swapping in the visible underline on selection doesn't
   shift the text vertically. Sits inside TabFilterButton — the underline therefore only spans
   the text width, not the full button (including its padding). */
const TabFilterLabel = styled.span<{ selected?: boolean }>`
  display: inline-block;
  line-height: 1;
  padding-bottom: 2px;
  border-bottom: 0.5px solid
    ${({ selected }) =>
      selected ? 'var(--way-colors-contrastColorShades-100)' : 'transparent'};
`;

/* Inline filter-row buttons. `$asTab` toggles the tab-style overrides on/off: when true the
   filter row reads as underlined text tabs (schedule view); when false the overrides drop and
   the button falls through to SecondaryButton's default pill styling (original non-schedule
   design). The pill's selected state is provided by the `secondary` variant itself. */
const TabFilterButton = styled(SecondaryButton)<{
  selected?: boolean;
  $asTab?: boolean;
}>`
  ${({ $asTab = true }) => $asTab && tabStyle}

  &:focus-visible {
    border-radius: 4px;
  }
`;

const TabFilterLink = styled(SecondaryButtonLink)<{
  selected?: boolean;
  $asTab?: boolean;
}>`
  ${({ $asTab = true }) => $asTab && tabStyle}

  &:focus-visible {
    border-radius: 4px;
  }
`;

/* The date control sits in the row but is a dropdown trigger, not a selectable tab, so it never
   shows the active underline — its active state is conveyed by the date-range text + icon colour.
   Adds the icon + label gap on top of the base button styling. */
const DateButton = styled(TabFilterButton)`
  gap: 8px;
`;
